import socketClient from '../socket';
import { fetchJson, buildApiUrl } from '../utils/api';
import { normalizeAttachments } from '../utils/attachments';

export const fromServerQuestion = (q = {}) => {
    const playsets = q.playsets ?? [];
    const normalizedAttachments = normalizeAttachments(q.attachments ?? [], q.id);
    return {
        ...q,
        attachments: normalizedAttachments,
        note: q.note ?? '',
        lastPlayedAt: q.lastPlayedAt ?? null,
        playsets,
        playsetIds: playsets.map((p) => p.id),
        gespielt: q.gespielt,
    };
};

export const toServerQuestionPayload = (ui = {}) => {
    const payload = {
        frage: ui.frage,
        antworten: ui.antworten,
        richtigeAntwort: ui.richtigeAntwort,
    };

    if (ui.kategorie !== undefined) payload.kategorie = ui.kategorie;
    if (ui.schwierigkeitsgrad !== undefined) payload.schwierigkeitsgrad = ui.schwierigkeitsgrad;

    payload.attachments = ui.attachments ?? [];
    payload.note = ui.note ?? '';
    payload.playsetIds = ui.playsetIds ?? [];

    if (ui.gespielt !== undefined) {
        payload.gespielt = ui.gespielt;
    }

    return payload;
};

export async function loadQuestions() {
    const response = await fetch(buildApiUrl('/api/questions'));
    const contentType = response.headers.get('content-type') || '';

    if (!response.ok) {
        const text = await response.text();
        const snippet = text.slice(0, 120);
        throw new Error(`Failed to load questions (${response.status}). ${snippet}`);
    }

    if (!contentType.includes('application/json')) {
        const text = await response.text();
        const snippet = text.slice(0, 120);
        throw new Error(
            `Expected JSON from API but got ${contentType || 'unknown'}. Base URL likely misconfigured. Snippet: ${snippet}`
        );
    }

    const data = await response.json();
    return Array.isArray(data) ? data.map(fromServerQuestion) : [];
}

export async function createQuestion(question) {
    const payload = toServerQuestionPayload(question);
    const created = await fetchJson('/api/questions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    return fromServerQuestion(created);
}

export async function updateQuestion(id, question) {
    const payload = toServerQuestionPayload(question);
    const updated = await fetchJson(`/api/questions/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    return fromServerQuestion(updated);
}

export async function deleteQuestion(id) {
    await fetchJson(`/api/questions/${id}`, { method: 'DELETE' });
}

export async function resetPlayedQuestions() {
    await fetchJson('/api/questions/reset-gespielt', { method: 'PUT' });
}

export async function loadPlaysets() {
    const response = await fetch(buildApiUrl('/api/playsets'));
    const contentType = response.headers.get('content-type') || '';

    if (!response.ok) {
        const text = await response.text();
        const snippet = text.slice(0, 120);
        throw new Error(`Failed to load playsets (${response.status}). ${snippet}`);
    }

    if (!contentType.includes('application/json')) {
        const text = await response.text();
        const snippet = text.slice(0, 120);
        throw new Error(
            `Expected JSON from API but got ${contentType || 'unknown'}. Base URL likely misconfigured. Snippet: ${snippet}`
        );
    }

    const data = await response.json();
    return Array.isArray(data) ? data : [];
}

export async function createOrUpdatePlayset(payload) {
    if (payload?.id) {
        const updated = await fetchJson(`/api/playsets/${payload.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        return updated;
    }

    const created = await fetchJson('/api/playsets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    return created;
}

export async function deletePlayset(id) {
    await fetchJson(`/api/playsets/${id}`, { method: 'DELETE' });
}

export async function updatePlaysetStatus(id, status) {
    return fetchJson(`/api/playsets/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
    });
}

export async function duplicatePlayset(id, options = {}) {
    return fetchJson(`/api/playsets/${id}/duplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(options),
    });
}

export async function loadShowMatrix(playsetId) {
    return fetchJson(`/api/playsets/${playsetId}/matrix`);
}

export async function saveShowMatrix(playsetId, matrix) {
    return fetchJson(`/api/playsets/${playsetId}/matrix`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ matrix }),
    });
}

export async function clearShowMatrix(playsetId) {
    return fetchJson(`/api/playsets/${playsetId}/matrix`, { method: 'DELETE' });
}

export async function loadShowFinale(playsetId) {
    return fetchJson(`/api/playsets/${playsetId}/finale`);
}

export async function saveShowFinale(playsetId, payload) {
    return fetchJson(`/api/playsets/${playsetId}/finale`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
}

export async function loadActiveShow() {
    return fetchJson('/api/active-show');
}

export async function setActiveShow(playsetId) {
    return fetchJson('/api/active-show', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playsetId }),
    });
}

export function subscribeActiveShow(socket = socketClient, onChange) {
    if (typeof onChange !== 'function') return () => {};
    const listener = (payload) => onChange(payload?.playsetId ?? null);
    socket.on('show:active', listener);
    return () => socket.off('show:active', listener);
}

export function subscribeQuizState(socket = socketClient, handlers) {
    const { onQuizState, onQuestionSelected } = handlers || {};

    socket.emit('getQuizState');

    const quizStateListener = (state) => {
        onQuizState && onQuizState(state);
    };
    const questionSelectedListener = (payload) => {
        onQuestionSelected && onQuestionSelected(payload);
    };

    socket.on('quizState', quizStateListener);
    socket.on('questionSelected', questionSelectedListener);

    const onConnect = () => socket.emit('getQuizState');
    socket.on('connect', onConnect);

    return () => {
        socket.off('quizState', quizStateListener);
        socket.off('questionSelected', questionSelectedListener);
        socket.off('connect', onConnect);
    };
}

const getUploadUrl = () => {
    const explicit = process.env.REACT_APP_UPLOAD_URL;
    if (explicit) return explicit;
    // Ohne explizite Server-URL: relativer Pfad funktioniert per Proxy oder Same-Origin
    const path = process.env.REACT_APP_UPLOAD_PATH || '/api/upload';
    return buildApiUrl(path);
};

export const isUploadConfigured = () => !!getUploadUrl();

export async function uploadAttachment(file) {
    const uploadUrl = getUploadUrl();
    if (!uploadUrl) {
        throw new Error('Upload not configured');
    }

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
    });

    const contentType = response.headers.get('content-type') || '';
    const responseText = await response.text();
    const snippet = responseText.slice(0, 120);

    if (!response.ok) {
        throw new Error(`Upload failed (status ${response.status}). ${snippet}`);
    }

    if (!contentType.includes('application/json')) {
        throw new Error(
            `Expected JSON upload response with { url, originalName, mime, size } but got ${contentType || 'unknown'}. Snippet: ${snippet}`
        );
    }

    let data = null;
    try {
        data = JSON.parse(responseText);
    } catch (error) {
        throw new Error(`Upload response could not be parsed. Snippet: ${snippet}`);
    }

    if (data?.url) {
        const size = Number(data.size);
        return {
            url: data.url,
            originalName: data.originalName || '',
            mime: data.mime || '',
            size: Number.isFinite(size) ? size : 0,
        };
    }

    throw new Error(`Upload response missing url. Snippet: ${snippet}`);
}
